import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format date
// Pass `timeZone` (IANA, e.g. "America/Los_Angeles") to render in the user's
// chosen zone. Omit it to use the browser default.
export function formatDate(date, timeZone) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  });
}

// Format date with time
// Pass `timeZone` (IANA) to render in the user's chosen zone.
export function formatDateTime(date, timeZone) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

// Generate unique ID
export function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

// Truncate text
export function truncate(str, length = 50) {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
}

// Copy to clipboard
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// Download file
export function downloadFile(data, filename, type = 'application/json') {
  const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Form element types with labels and icons
export const FORM_ELEMENT_TYPES = {
  short_text: { label: 'Short Text', icon: 'Type', category: 'input' },
  long_text: { label: 'Long Text', icon: 'AlignLeft', category: 'input' },
  email: { label: 'Email', icon: 'Mail', category: 'input' },
  phone: { label: 'Phone', icon: 'Phone', category: 'input' },
  url: { label: 'URL', icon: 'Link', category: 'input' },
  number: { label: 'Number', icon: 'Hash', category: 'input' },
  multiple_choice: { label: 'Multiple Choice', icon: 'CircleDot', category: 'choice' },
  checkboxes: { label: 'Checkboxes', icon: 'CheckSquare', category: 'choice' },
  dropdown: { label: 'Dropdown', icon: 'ChevronDown', category: 'choice' },
  date: { label: 'Date', icon: 'Calendar', category: 'input' },
  time: { label: 'Time', icon: 'Clock', category: 'input' },
  file_upload: { label: 'File Upload', icon: 'Upload', category: 'input' },
  rating: { label: 'Rating', icon: 'Star', category: 'choice' },
  linear_scale: { label: 'Linear Scale', icon: 'Sliders', category: 'choice' },
  signature: { label: 'Signature', icon: 'PenTool', category: 'input' },
  heading: { label: 'Heading', icon: 'Heading', category: 'content' },
  statement: { label: 'Statement', icon: 'FileText', category: 'content' },
  divider: { label: 'Divider', icon: 'Minus', category: 'content' },
  image: { label: 'Image', icon: 'Image', category: 'content' },
  video: { label: 'Video', icon: 'Video', category: 'content' }
};

// Get form URL
export function getFormUrl(formId, shortId) {
  return `${window.location.origin}/f/${shortId || formId}`;
}

// Get embed code
export function getEmbedCode(formId, shortId) {
  const id = shortId || formId;
  return `<div id="ff-${id}"></div>
<script src="${window.location.origin}/embed.js" data-form-id="${id}"></script>`;
}
